(() => {
  // Match any element with data-product-id on the blog article page (covers
  // legacy .article-related-products too). Product details come from a
  // <script id="product-data"> JSON block rendered into the article, so we
  // can populate discount, code, description, learnMoreUrl and variantId
  // without hitting the product API. API fetch remains as a fallback.
  const SELECTOR = '.article [data-product-id]';

  const HANDLE_ALIASES = {
    'arzopa-z3fc': 'arzopa-z3fc-16-1-180hz-2560x1440-qhd-portable-gaming-monitor',
    'arzopa-z1fc': 'arzopa-z1fc-gray-144hz-portable-gaming-monitor-16-1-screen',
    'arzopa-d14': 'arzopa-wifi-14-fhd-ips-touch-screen-digital-picture-frame',
    'arzopa-d10': 'd10-gold',
  };

  const formatMoney = (cents) => {
    if (window.FoxTheme?.Currency?.formatMoney) {
      return window.FoxTheme.Currency.formatMoney(cents, window.FoxTheme.settings.moneyFormat);
    }
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  };

  const productUrl = (handle) => {
    const root = window.FoxTheme?.routes?.root_url || '/';
    return `${root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}`;
  };

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  };

  // Parse the inline <script id="product-data"> JSON once.
  const readInlineProducts = () => {
    const script = document.getElementById('product-data');
    if (!script) return null;
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed.products) ? parsed.products : [];
    } catch (error) {
      console.error('Failed to parse product-data JSON', error);
      return [];
    }
  };

  const COPY_ICON_SVG =
    '<svg class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="18" height="18" aria-hidden="true">' +
    '<path fill="currentColor" d="M128 128v640h224v-64H192V192h384v32h64V128z m256 128v640h512V256z m64 64h384v512H448z"></path>' +
    '</svg>';

  const copyToClipboard = async (text, button) => {
    if (!text || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(text);
      if (button) {
        const original = button.getAttribute('aria-label') || '';
        button.setAttribute('aria-label', 'Copied');
        setTimeout(() => button.setAttribute('aria-label', original), 1500);
      }
    } catch (error) {
      console.warn('Clipboard copy failed', error);
    }
  };

  const renderFromData = (placeholder, data) => {
    const card = createElement('article', 'article-inline-product article-product-feature-card article-product-feature-card--full');

    const media = createElement('div', 'article-product-feature-card__media');
    if (data.image) {
      const img = createElement('img');
      img.src = data.image;
      img.alt = data.title || '';
      img.loading = 'lazy';
      img.width = 600;
      img.height = 600;
      media.appendChild(img);
    }

    const info = createElement('div', 'article-product-feature-card__info');

    if (data.title) {
      const titleEl = createElement('h3', 'article-product-feature-card__title', data.title);
      info.appendChild(titleEl);
    }

    if (data.price || data.originPrice) {
      const prices = createElement('div', 'article-product-feature-card__prices');
      if (data.price) {
        prices.appendChild(createElement('span', 'article-product-feature-card__price', '$' + data.price));
      }
      if (data.originPrice) {
        prices.appendChild(createElement('s', 'article-product-feature-card__compare-price', '$' + data.originPrice));
      }
      info.appendChild(prices);
    }

    if (data.code) {
      const codeRow = createElement('div', 'article-product-feature-card__code');
      codeRow.appendChild(createElement('span', 'article-product-feature-card__code-label', 'Code:'));
      codeRow.appendChild(createElement('span', 'article-product-feature-card__code-text', data.code));
      const copyBtn = createElement('button', 'article-product-feature-card__code-copy');
      copyBtn.type = 'button';
      copyBtn.setAttribute('aria-label', 'Copy code');
      copyBtn.innerHTML = COPY_ICON_SVG;
      copyBtn.addEventListener('click', () => copyToClipboard(data.code, copyBtn));
      codeRow.appendChild(copyBtn);
      info.appendChild(codeRow);
    }

    if (data.description) {
      const desc = createElement('div', 'article-product-feature-card__description');
      desc.innerHTML = data.description;
      info.appendChild(desc);
    }

    if (data.variantId || data.learnMoreUrl) {
      const actions = createElement('div', 'article-product-card__actions article-product-feature-card__actions');
      const buyHref = data.variantId
        ? (window.FoxTheme?.routes?.root_url || '/') + 'cart/' + data.variantId + ':1'
        : (data.learnMoreUrl || '#');
      const buyBtn = createElement('div', 'btn btn--gradient article-product-feature-card__buy');
      buyBtn.setAttribute('role', 'button');
      buyBtn.setAttribute('tabindex', '0');
      buyBtn.addEventListener('click', () => { window.location.href = buyHref; });
      buyBtn.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          window.location.href = buyHref;
        }
      });
      buyBtn.appendChild(createElement('span', 'btn__text', 'Buy'));
      actions.appendChild(buyBtn);

      const learnHref = data.learnMoreUrl || '#';
      const learnBtn = createElement('div', 'btn btn--gradient article-product-feature-card__learn');
      learnBtn.setAttribute('role', 'button');
      learnBtn.setAttribute('tabindex', '0');
      learnBtn.setAttribute('style', '--custom-button-color: 0, 0, 0;--custom-button-accent: 0, 0, 0;--custom-button-text: 255, 255, 255;');
      learnBtn.addEventListener('click', () => { window.location.href = learnHref; });
      learnBtn.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          window.location.href = learnHref;
        }
      });
      learnBtn.appendChild(createElement('span', 'btn__text', 'Learn More'));
      actions.appendChild(learnBtn);

      info.appendChild(actions);
    }

    card.append(media, info);
    placeholder.replaceChildren(card);
    if (data.discount) {
      placeholder.appendChild(createElement('div', 'article-product-feature-card__badge', data.discount));
    }
    placeholder.classList.add('article-product-feature-card-placeholder');
    placeholder.dataset.productLoaded = 'true';
  };

  const renderFromApi = async (placeholder, handle, link) => {
    const variantId = placeholder.dataset.variantId?.trim();
    const customTitle = placeholder.dataset.title?.trim();
    const customImage = placeholder.dataset.image?.trim();
    const customPrice = placeholder.dataset.price?.trim();
    const customComparePrice = placeholder.dataset.comparePrice?.trim();
    const customDiscount = placeholder.dataset.discount?.trim();
    const customCode = placeholder.dataset.code?.trim();
    const customDescription = placeholder.dataset.description?.trim();
    const customLearnLabel = placeholder.dataset.learnLabel?.trim() || 'Learn More';
    const customBuyLabel = placeholder.dataset.buyLabel?.trim() || 'Buy';

    const card = createElement('article', 'article-inline-product article-product-feature-card article-product-feature-card--full');

    const media = createElement('div', 'article-product-feature-card__media');

    const response = await fetch(productUrl(handle) + '.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Product request failed: ' + response.status);
    const product = await response.json();
    const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
    const finalVariantId = variantId || variant?.id;
    const productHref = link || product.url || productUrl(handle);
    const buyHref = finalVariantId ? productUrl(handle) + '/cart/' + finalVariantId + ':1' : productHref;

    if (customImage || product.featured_image) {
      const img = createElement('img');
      img.src = customImage || product.featured_image;
      img.alt = customTitle || product.title || '';
      img.loading = 'lazy';
      img.width = 600;
      img.height = 600;
      media.appendChild(img);
    }

    const info = createElement('div', 'article-product-feature-card__info');

    if (customTitle || product.title) {
      info.appendChild(createElement('h3', 'article-product-feature-card__title', customTitle || product.title));
    }

    const prices = createElement('div', 'article-product-feature-card__prices');
    const priceValue = variant?.price ?? product.price;
    const comparePriceValue = variant?.compare_at_price ?? product.compare_at_price;
    if (customPrice || priceValue) {
      prices.appendChild(createElement('span', 'article-product-feature-card__price', customPrice || formatMoney(priceValue)));
    }
    const compareText = customComparePrice || (comparePriceValue && comparePriceValue > priceValue ? formatMoney(comparePriceValue) : '');
    if (compareText) {
      prices.appendChild(createElement('s', 'article-product-feature-card__compare-price', compareText));
    }
    info.appendChild(prices);

    if (customCode) {
      const codeRow = createElement('div', 'article-product-feature-card__code');
      codeRow.appendChild(createElement('span', 'article-product-feature-card__code-label', 'Code:'));
      codeRow.appendChild(createElement('span', 'article-product-feature-card__code-text', customCode));
      const copyBtn = createElement('button', 'article-product-feature-card__code-copy');
      copyBtn.type = 'button';
      copyBtn.setAttribute('aria-label', 'Copy code');
      copyBtn.innerHTML = COPY_ICON_SVG;
      copyBtn.addEventListener('click', () => copyToClipboard(customCode, copyBtn));
      codeRow.appendChild(copyBtn);
      info.appendChild(codeRow);
    }

    if (customDescription) {
      const desc = createElement('div', 'article-product-feature-card__description');
      desc.innerHTML = customDescription;
      info.appendChild(desc);
    }

    const actions = createElement('div', 'article-product-card__actions article-product-feature-card__actions');
    const buyBtn = createElement('div', 'btn btn--gradient article-product-feature-card__buy');
    buyBtn.setAttribute('role', 'button');
    buyBtn.setAttribute('tabindex', '0');
    buyBtn.addEventListener('click', () => { window.location.href = buyHref; });
    buyBtn.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        window.location.href = buyHref;
      }
    });
    buyBtn.appendChild(createElement('span', 'btn__text', customBuyLabel));
    actions.appendChild(buyBtn);
    const learnBtn = createElement('div', 'btn btn--gradient article-product-feature-card__learn');
    learnBtn.setAttribute('role', 'button');
    learnBtn.setAttribute('tabindex', '0');
    learnBtn.setAttribute('style', '--custom-button-color: 0, 0, 0;--custom-button-accent: 0, 0, 0;--custom-button-text: 255, 255, 255;');
    learnBtn.addEventListener('click', () => { window.location.href = productHref; });
    learnBtn.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        window.location.href = productHref;
      }
    });
    learnBtn.appendChild(createElement('span', 'btn__text', customLearnLabel));
    actions.appendChild(learnBtn);
    info.appendChild(actions);

    card.append(media, info);
    placeholder.replaceChildren(card);
    if (customDiscount) {
      placeholder.appendChild(createElement('div', 'article-product-feature-card__badge', customDiscount));
    }
    placeholder.classList.add('article-product-feature-card-placeholder');
    placeholder.dataset.productLoaded = 'true';
  };

  const loadPlaceholder = async (placeholder, inlineProducts) => {
    const requestedHandle = placeholder.dataset.productId?.trim();
    if (!requestedHandle) return false;
    const handle = HANDLE_ALIASES[requestedHandle.toLowerCase()] || requestedHandle;

    const inlineMatch = inlineProducts?.find(
      (item) => (item['product-id'] || '').toLowerCase() === requestedHandle.toLowerCase(),
    );

    placeholder.classList.add('is-loading');
    try {
      if (inlineMatch) {
        renderFromData(placeholder, inlineMatch);
      } else {
        await renderFromApi(placeholder, handle, null);
      }
      return true;
    } catch (error) {
      console.error('Failed to render product card', error);
      placeholder.remove();
      return false;
    } finally {
      placeholder.classList.remove('is-loading');
    }
  };

  const init = async (root = document) => {
    const placeholders = [...root.querySelectorAll(SELECTOR)].filter((item) => !item.dataset.productLoaded);
    if (!placeholders.length) return;
    const inlineProducts = readInlineProducts();
    const results = await Promise.all(placeholders.map((ph) => loadPlaceholder(ph, inlineProducts)));
    const module = placeholders[0].closest('.article-related-products');
    if (module && !results.some(Boolean)) module.hidden = true;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
